import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { isKanbanBoardItemEqual } from './utils/isKanbanBoardItemEqual';
export const KanbanBoardContext = createContext(null);
export function useKanbanBoardContext() {
    const context = useContext(KanbanBoardContext);
    if (!context) {
        throw new Error('useKanbanBoardContext must be used within the KanbanBoardProvider component');
    }
    return context;
}
export const KanbanBoardProvider = props => {
    const { items, columns, disabled = false, children, findEvent, setItems, onSelectItem } = props;
    const [dragItem, setDragItem] = useState();
    const [dropItem, setDropItemState] = useState();
    const [loadingItems, setLoadingItems] = useState([]);
    const dropItemRef = useRef(dropItem);
    // set both ref and state values
    const setDropItem = useCallback(value => {
        dropItemRef.current =
            typeof value === 'function' ? value(dropItemRef.current) : value;
        setDropItemState(dropItemRef.current);
    }, []);
    const getDragState = useCallback((options) => {
        const id = options === null || options === void 0 ? void 0 : options.id.toString();
        const index = items.findIndex(item => item.id.toString() === id);
        const item = index > -1 ? items[index] : undefined;
        const column = options && columns.find(column => column.id === options.columnId);
        return item && column && { index, item, column };
    }, [columns, items]);
    const drag = useMemo(() => {
        return getDragState(dragItem);
    }, [dragItem, getDragState]);
    const drop = useMemo(() => {
        const state = dropItem && getDragState(dropItem.item);
        const dropColumn = dropItem && columns.find(column => column.id === dropItem.dropColumnId);
        return state && dropColumn && Object.assign(Object.assign({}, state), { dropColumn });
    }, [columns, dropItem, getDragState]);
    const isItemLoading = useCallback((item) => {
        return loadingItems.some(loadingItem => {
            return isKanbanBoardItemEqual(loadingItem, item);
        });
    }, [loadingItems]);
    const setItemLoading = useCallback((item, loading) => {
        setLoadingItems(loadingItems => {
            const index = loadingItems.findIndex(loadingItem => {
                return isKanbanBoardItemEqual(loadingItem, item);
            });
            if (index === -1 && loading) {
                return loadingItems.concat(item);
            }
            else if (index > -1 && !loading) {
                const copy = Array.from(loadingItems);
                copy.splice(index, 1);
                return copy;
            }
            return loadingItems;
        });
    }, []);
    const findBoardItem = useCallback(match => {
        const id = typeof match === 'string' ? match : match.id.toString();
        const index = items.findIndex(item => item.id.toString() === id);
        return { index, item: items[index] };
    }, [items]);
    const moveBoardItem = useCallback((match, columnId, index) => {
        const id = match.id.toString();
        setItems(items => {
            const itemIndex = items.findIndex(item => item.id.toString() === id);
            const item = items[itemIndex];
            if (!item || item.columnId === columnId) {
                return items;
            }
            const targetIndex = typeof index === 'number' && index > -1 && index < items.length
                ? index
                : items.length - 1; // move to last index
            const copy = Array.from(items);
            copy.splice(itemIndex, 1);
            copy.splice(targetIndex, 0, Object.assign(Object.assign({}, item), { columnId }));
            return copy;
        });
    }, [setItems]);
    const clearDrop = useCallback((undoMove = false) => {
        // use `dropItemRef` instead of `setDropItem(callback)`
        // to get the current `dropItem` value to avoid console errors
        const drop = dropItemRef.current;
        if (undoMove && drop) {
            // move the task back to initial column
            const { item } = drop;
            moveBoardItem(item, item.columnId, item.index);
        }
        setDropItem(undefined);
    }, [moveBoardItem, setDropItem]);
    const context = useMemo(() => {
        return {
            drag,
            drop,
            items,
            columns,
            disabled,
            loadingItems,
            clearDrop,
            findEvent,
            findBoardItem,
            isItemLoading,
            moveBoardItem,
            onSelectItem,
            setDragItem,
            setDropItem,
            setItemLoading
        };
    }, [
        drag,
        drop,
        items,
        columns,
        disabled,
        loadingItems,
        clearDrop,
        findEvent,
        findBoardItem,
        isItemLoading,
        moveBoardItem,
        onSelectItem,
        setDropItem,
        setItemLoading
    ]);
    return (React.createElement(KanbanBoardContext.Provider, { value: context }, children));
};
//# sourceMappingURL=KanbanBoardContext.js.map